import React, { useEffect, useState } from "react";
import { useRecoilValue } from "recoil";
import { useHistory, useLocation } from "react-router-dom";

import { permissionsSelector } from "../../Login/selectors/permissionSelector";
import { PERMISSIONS } from "../../PermissionsConstants";
import {
  busLicensesAddState,
  busLicensesEditState,
  busLicensesShowState,
} from "./atoms/atoms";

import {
  DownloadBLPExcelRequest,
  useDownloadBLPExcel,
} from "./api/downloadBLPExcel";
import { toast } from "../../../../src/components/toastNotification";

import BLPDashboard from "./view/BLPDashboard";
import { BusLicensesAndPermitsTable } from "./view/BusLicensesAndPermitsTable";
import type { BLPNotificationRouteState } from "./view/BusLicensesAndPermitsTable";
import ViewAllRecent from "./view/Dashboard/ViewAllRecent";

interface Props {}

type BLPView = "dashboard" | "table" | "recent";

type BLPRouteLocation = ReturnType<typeof useLocation> & {
  state?: BLPNotificationRouteState | null;
};

const BLP_ROUTE = "/officesettings/buslicensesandpermits";

const getViewFromUrlParam = (viewFromUrlParam: string | null): BLPView => {
  if (viewFromUrlParam === "table") {
    return "table";
  }

  if (viewFromUrlParam === "recent") {
    return "recent";
  }

  return "dashboard";
};

const hasValue = (value: unknown): boolean =>
  value !== null && value !== undefined && value !== "";

const isNotificationRouteState = (
  value: BLPNotificationRouteState | null | undefined
): value is BLPNotificationRouteState => {
  if (!value) {
    return false;
  }

  return hasValue(value.recordId) || value.action === "VendorCancelToBlp";
};

export const BusLicenseAndPermit: React.FC<Props> = () => {
  const viewPermission = PERMISSIONS.permission_buslicensesandpermits_view;

  const userPermissions = useRecoilValue(permissionsSelector);

  const isAddState = useRecoilValue(busLicensesAddState);

  const isEditState = useRecoilValue(busLicensesEditState);

  const isShowState = useRecoilValue(busLicensesShowState);

  const hasBLPTableState = isAddState || isEditState || isShowState;

  const history = useHistory();

  const location = useLocation() as BLPRouteLocation;

  const incomingNotificationRouteState = location.state ?? null;

  const [notificationRouteState, setNotificationRouteState] =
    useState<BLPNotificationRouteState | null>(() =>
      isNotificationRouteState(incomingNotificationRouteState)
        ? incomingNotificationRouteState
        : null
    );

  useEffect(() => {
    if (isNotificationRouteState(incomingNotificationRouteState)) {
      setNotificationRouteState(incomingNotificationRouteState);
    }
  }, [incomingNotificationRouteState]);

  const searchParams = new URLSearchParams(location.search);

  const viewFromUrlParam = searchParams.get("view");

  const statusFromUrlParam = searchParams.get("status");

  const recordIdFromUrlParam = searchParams.get("recordId");

  const hasNotificationRedirect = isNotificationRouteState(
    notificationRouteState
  );

  const resolvedView: BLPView =
    hasNotificationRedirect || hasBLPTableState
      ? "table"
      : getViewFromUrlParam(viewFromUrlParam);

  const [view, setView] = useState<BLPView>(resolvedView);

  const [statusFilter, setStatusFilter] = useState<string | null>(
    hasNotificationRedirect || hasBLPTableState ? null : statusFromUrlParam
  );

  const [recordIdFilter, setRecordIdFilter] = useState<string | null>(
    hasNotificationRedirect || hasBLPTableState ? null : recordIdFromUrlParam
  );

  useEffect(() => {
    if (hasNotificationRedirect || hasBLPTableState) {
      setView("table");
      setStatusFilter(null);
      setRecordIdFilter(null);

      return;
    }

    setView(getViewFromUrlParam(viewFromUrlParam));

    setStatusFilter(statusFromUrlParam);

    setRecordIdFilter(recordIdFromUrlParam);
  }, [
    hasBLPTableState,
    hasNotificationRedirect,
    recordIdFromUrlParam,
    statusFromUrlParam,
    viewFromUrlParam,
  ]);

  const navigateToView = (
    next: BLPView,
    status?: string,
    recordId?: string
  ) => {
    setNotificationRouteState(null);

    setView(next);
    setStatusFilter(status ?? null);
    setRecordIdFilter(recordId ?? null);

    const nextSearchParams = new URLSearchParams();

    if (next !== "dashboard") {
      nextSearchParams.set("view", next);
    }

    if (next === "table") {
      if (recordId) {
        nextSearchParams.set("recordId", recordId);
      } else if (status) {
        nextSearchParams.set("status", status);
      }
    }

    const nextSearch = nextSearchParams.toString();

    history.push({
      pathname: BLP_ROUTE,
      search: nextSearch ? `?${nextSearch}` : "",
    });
  };

  const {
    mutateAsync: downloadBLPExcelFile,

    isLoading: downloadingTable,
  } = useDownloadBLPExcel();

  const handleTableDownload = async (
    request?: DownloadBLPExcelRequest
  ): Promise<void> => {
    try {
      await downloadBLPExcelFile(request);

      toast.fire("Success", "File downloaded successfully", "success");
    } catch {
      toast.fire("Error", "Failed to download file", "error");
    }
  };

  const handleRecordClick = (recordIds: string[]) => {
    const validRecordIds = Array.from(
      new Set(
        (recordIds ?? [])
          .map((recordId) => String(recordId).trim())
          .filter((recordId) => recordId !== "")
      )
    );

    if (validRecordIds.length === 0) {
      return;
    }

    if (validRecordIds.length === 1) {
      navigateToView("table", undefined, validRecordIds[0]);

      return;
    }

    const recentActivityState: BLPNotificationRouteState = {
      recordId: validRecordIds.join("."),
      action: "RecentActivityToBlp",
    };

    setNotificationRouteState(recentActivityState);

    setView("table");

    setStatusFilter(null);

    setRecordIdFilter(null);

    history.push({
      pathname: BLP_ROUTE,
      search: "?view=table",
      state: recentActivityState,
    });
  };

  let viewContent: React.ReactNode;

  if (view === "dashboard") {
    viewContent = (
      <BLPDashboard
        onGoToTable={(status?: string) => navigateToView("table", status)}
        onViewAllRecent={() => navigateToView("recent")}
        onRecordClick={handleRecordClick}
      />
    );
  } else if (view === "recent") {
    viewContent = (
      <ViewAllRecent
        onBackToDashboard={() => navigateToView("dashboard")}
        onRecordClick={handleRecordClick}
      />
    );
  } else {
    viewContent = (
      <BusLicensesAndPermitsTable
        onBackToDashboard={() => navigateToView("dashboard")}
        handleDownload={handleTableDownload}
        downloading={downloadingTable}
        initialStatus={statusFilter}
        initialRecordId={recordIdFilter}
        notificationRouteState={notificationRouteState}
        onCloseRecordDetails={() => navigateToView("table")}
      />
    );
  }

  if (!userPermissions.includes(viewPermission)) {
    return null;
  }

  return (
    <div
      style={{
        padding: "16px 26px",
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        marginBottom: "8px",
        marginTop: "8px",
      }}
    >
      {viewContent}
    </div>
  );
};
